import { Coffee, Minus, Plus } from "lucide-react";

// Branded Cake Society x Dunkin' catering add-on picker.
// value: [{ key, qty, variant }]  onChange(next)
// catalog items: { key, label, price_label, serves, variants:[], variant_label, cost? }
export function DunkinAddons({ catalog = [], value = [], onChange, showCost = false }) {
  const get = (key) => value.find((v) => v.key === key);

  const update = (key, patch) => {
    const item = catalog.find((c) => c.key === key);
    const existing = get(key);
    let next;
    if (existing) {
      next = value.map((v) => (v.key === key ? { ...v, ...patch } : v));
    } else {
      const variant = item?.variants?.length ? item.variants[0] : "";
      next = [...value, { key, qty: 0, variant, ...patch }];
    }
    onChange(next.filter((v) => Number(v.qty) > 0));
  };

  const setQty = (key, qty) => update(key, { qty: Math.max(0, Math.min(99, qty)) });
  const setVariant = (key, variant) => {
    // ensure the item exists (qty at least 1) when a flavor is chosen
    const existing = get(key);
    update(key, { variant, qty: existing ? existing.qty : 1 });
  };

  return (
    <div data-testid="dunkin-addons" className="border-2 border-[#121212] bg-white overflow-hidden">
      {/* Branded header */}
      <div className="bg-[#FF6E0C] px-6 py-5 border-b-2 border-[#121212] relative overflow-hidden">
        <div className="absolute -right-6 -top-6 w-28 h-28 rounded-full bg-[#E0218A]/30" />
        <div className="absolute right-10 bottom-[-30px] w-20 h-20 rounded-full bg-white/20" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 text-white">
            <Coffee size={18} strokeWidth={2.5} />
            <span className="text-[11px] tracking-[0.28em] uppercase font-bold">Cake Society × Dunkin'</span>
          </div>
          <h3 className="mt-1 font-serif font-black text-white leading-[0.95] text-3xl sm:text-4xl">
            Cake Society Runs on Dunkin'
          </h3>
          <p className="mt-2 text-white/95 text-sm font-semibold max-w-lg">
            Exclusive Dunkin' catering add-ons available for Cake Society parties.
          </p>
        </div>
      </div>

      {/* Items */}
      <div className="divide-y divide-stone-200">
        {catalog.map((a) => {
          const sel = get(a.key);
          const qty = sel ? Number(sel.qty) : 0;
          const active = qty > 0;
          const lineTotal = (Number(a.price ?? 0) || parseFloat((a.price_label || "").replace(/[^0-9.]/g, "")) || 0) * qty;
          return (
            <div key={a.key} data-testid={`dunkin-item-${a.key}`}
              className={`p-5 transition-colors ${active ? "bg-[#FF6E0C]/5" : "bg-white"}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <p className="font-bold text-[#121212]">{a.label}</p>
                  <p className="text-sm text-[#121212]/60 mt-0.5">{a.serves}</p>
                  {showCost && a.cost != null && (
                    <p className="text-[11px] text-[#121212]/50 mt-1">
                      Wholesale cost ${Number(a.cost).toFixed(2)} · margin ${(Number(a.price) - Number(a.cost)).toFixed(2)} each
                      {qty > 0 && <> · total margin ${((Number(a.price) - Number(a.cost)) * qty).toFixed(2)}</>}
                    </p>
                  )}
                </div>
                <span className="font-serif font-black text-[#121212] whitespace-nowrap">{a.price_label}</span>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-3">
                {/* qty stepper */}
                <div className="inline-flex items-center border-2 border-[#121212]">
                  <button type="button" data-testid={`dunkin-minus-${a.key}`} onClick={() => setQty(a.key, qty - 1)}
                    className="px-2.5 py-2 hover:bg-[#121212] hover:text-white disabled:opacity-30" disabled={qty <= 0}>
                    <Minus size={14} />
                  </button>
                  <span data-testid={`dunkin-qty-${a.key}`} className="w-10 text-center font-bold text-[#121212] select-none">{qty}</span>
                  <button type="button" data-testid={`dunkin-plus-${a.key}`} onClick={() => setQty(a.key, qty + 1)}
                    className="px-2.5 py-2 hover:bg-[#121212] hover:text-white">
                    <Plus size={14} />
                  </button>
                </div>

                {/* variant / flavor */}
                {a.variants?.length > 0 && (
                  <div className="flex items-center gap-2">
                    {a.variant_label && <span className="text-[11px] tracking-[0.12em] uppercase font-bold text-[#121212]/55">{a.variant_label}</span>}
                    <select data-testid={`dunkin-variant-${a.key}`} value={sel?.variant || a.variants[0]}
                      onChange={(e) => setVariant(a.key, e.target.value)}
                      className="border-2 border-[#121212] bg-white px-3 py-1.5 text-sm font-semibold focus:outline-none">
                      {a.variants.map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>
                )}

                {qty > 0 && (
                  <span className="ml-auto text-sm font-bold text-[#FF6E0C]">${lineTotal.toFixed(2)}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <p className="px-5 py-3 text-[11px] text-[#121212]/55 bg-[#F2E9D8] border-t-2 border-[#121212]">
        Dunkin' catering is added to your final party balance — nothing is charged for it today.
      </p>
    </div>
  );
}
